"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/compat/router";
import { useEffect, useMemo, useState } from "react";
import { NavItem } from "@/components/nav-item";
import { StorefrontBreadcrumbs } from "@/components/storefront-breadcrumbs";
import { StorefrontFooter } from "@/components/storefront-footer";
import { StorefrontIcon } from "@/components/storefront-icon";
import { fraunces as mobileMenuLabelFont } from "@/fonts";
import { useFirebaseAuth } from "@/lib/firebase-auth-context";
import { useBreadcrumbTitleOverride } from "@/lib/breadcrumb-title-context";
import {
  buildBreadcrumbs,
  buildHeaderNavigationEntries,
  filterHeaderNavigation,
  isMenuNavigationItem,
  mergeMenuCategories,
  normalizePath,
  splitMobileNavigationEntries,
} from "@/lib/storefront-shell.mjs";
import type {
  HeaderNavigationActionEntry,
  HeaderNavigationContentEntry,
  HeaderNavigationEntry,
  MergedMenuCategory,
  StorefrontLayoutVariant,
  StorefrontShellData,
} from "@/lib/storefront-shell-types";
import type { StorefrontSiteConfig } from "@/lib/storefront-site";
import type { NavigationItem } from "@/lib/storefront-types";

type StorefrontChromeProps = {
  navigationItems: NavigationItem[];
  shellData?: StorefrontShellData;
  layoutVariant?: StorefrontLayoutVariant;
  site: StorefrontSiteConfig;
  children: React.ReactNode;
};

type MobileTreeProps = {
  item: NavigationItem;
  expandedKeys: string[];
  onToggle: (key: string) => void;
  onNavigate: () => void;
};

function MobileTree({
  item,
  expandedKeys,
  onToggle,
  onNavigate,
}: MobileTreeProps) {
  const isExpanded = expandedKeys.includes(item.key);

  if (item.children.length === 0) {
    return (
      <Link
        href={item.path}
        onClick={onNavigate}
        className="flex items-center justify-between rounded-[1.25rem] border border-white/10 bg-white/4 px-4 py-3 text-sm font-medium text-white/88 transition-colors hover:bg-white/8 hover:text-white"
      >
        <span className="inline-flex items-center gap-3">
          <StorefrontIcon
            asset={item.icon}
            context="navigation"
            label={item.label}
            className="h-4 w-4 text-white/84"
          />
          <span>{item.label}</span>
        </span>
        <span aria-hidden="true" className="text-white/55">
          &rarr;
        </span>
      </Link>
    );
  }

  return (
    <div className="rounded-[1.25rem] border border-white/10 bg-white/4">
      <button
        type="button"
        onClick={() => onToggle(item.key)}
        aria-expanded={isExpanded}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="inline-flex items-center gap-3 text-sm font-semibold text-white/92">
          <StorefrontIcon
            asset={item.icon}
            context="navigation"
            label={item.label}
            className="h-4 w-4 text-white/84"
          />
          <span>{item.label}</span>
        </span>
        <span
          aria-hidden="true"
          className={`text-sm text-white/65 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
        >
          v
        </span>
      </button>
      {isExpanded ? (
        <div className="space-y-2 border-t border-white/10 px-4 py-3">
          <Link
            href={item.path}
            onClick={onNavigate}
            className="block rounded-xl px-3 py-2 text-sm text-white/78 transition-colors hover:bg-white/8 hover:text-white"
          >
            Visit {item.label}
          </Link>
          {item.children.map((child) => (
            <div key={child.key} className="space-y-2">
              {child.children.length > 0 ? (
                <div className="rounded-xl border border-white/8 bg-white/4 p-3">
                  <p className="text-sm font-semibold text-white">{child.label}</p>
                  <div className="mt-2 space-y-2 border-l border-white/8 pl-3">
                    <Link
                      href={child.path}
                      onClick={onNavigate}
                      className="block rounded-xl px-3 py-2 text-sm text-white/78 transition-colors hover:bg-white/8 hover:text-white"
                    >
                      Visit {child.label}
                    </Link>
                    {child.children.map((grandchild) => (
                      <Link
                        key={grandchild.key}
                        href={grandchild.path}
                        onClick={onNavigate}
                        className="block rounded-xl px-3 py-2 text-sm text-white/80 transition-colors hover:bg-white/8 hover:text-white"
                      >
                        {grandchild.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  href={child.path}
                  onClick={onNavigate}
                  className="block rounded-xl px-3 py-2 text-sm text-white/78 transition-colors hover:bg-white/8 hover:text-white"
                >
                  {child.label}
                </Link>
              )}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MobileActionItem({
  entry,
  onNavigate,
}: {
  entry: HeaderNavigationActionEntry;
  onNavigate: () => void;
}) {
  return (
    <Link
      href={entry.path}
      onClick={onNavigate}
      className="flex w-full items-center justify-between rounded-[1.25rem] border border-white/10 bg-white/4 px-4 py-3 text-left text-sm font-medium text-white/88 transition-colors hover:bg-white/8 hover:text-white"
    >
      <span>{entry.label}</span>
      <span aria-hidden="true" className="text-white/55">
        &rarr;
      </span>
    </Link>
  );
}

function MobileCategoryLink({
  category,
  onNavigate,
}: {
  category: MergedMenuCategory;
  onNavigate: () => void;
}) {
  const imageAlt = category.image?.alt?.trim() || category.label;
  const fallbackLabel = category.label.trim().charAt(0).toUpperCase() || "A";
  const hasSummary = category.summary.trim().length > 0;

  return (
    <Link
      href={category.path}
      onClick={onNavigate}
      className="group flex items-center gap-3 rounded-[1.4rem] border storefront-border-soft bg-white px-3.5 py-3 shadow-[0_18px_40px_-34px_rgba(0,39,0,0.3)] transition-colors duration-150 hover:bg-[#f7fbf6] active-storefront-bg-primary-soft"
    >
      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-[1rem] border storefront-border-soft bg-[#eef6ec]">
        {category.image ? (
          <Image
            src={category.image.url}
            alt={imageAlt}
            fill
            sizes="64px"
            className="object-contain p-1.5"
          />
        ) : (
          <div className="storefront-text-primary flex h-full items-center justify-center bg-[radial-gradient(circle_at_top,#ffffff_0%,#eef6ec_70%)] text-lg font-semibold">
            {fallbackLabel}
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p
          className={`${mobileMenuLabelFont.className} text-[1.03rem] font-semibold leading-tight tracking-[0.01em] storefront-text-heading`}
        >
          {category.label}
        </p>
        {hasSummary ? (
          <p
            className="mt-1.5 text-[0.82rem] leading-5 storefront-text-body"
            style={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {category.summary}
          </p>
        ) : null}
      </div>

      <span
        aria-hidden="true"
        className="storefront-text-primary shrink-0 text-xl leading-none transition-transform duration-150 group-hover:translate-x-0.5"
      >
        &rarr;
      </span>
    </Link>
  );
}

function DesktopActionItem({ entry }: { entry: HeaderNavigationActionEntry }) {
  return (
    <Link
      href={entry.path}
      className="block px-[1.28rem] py-[1.05rem] text-[0.85rem] font-semibold text-white/84 transition-colors hover:bg-white/7 hover:text-white lg:uppercase lg:tracking-[0.24em]"
    >
      {entry.label}
    </Link>
  );
}

export function StorefrontChrome({
  navigationItems,
  shellData,
  layoutVariant = "default",
  site,
  children,
}: StorefrontChromeProps) {
  const router = useRouter();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const { user } = useFirebaseAuth();
  const signedIn = user !== null;
  // A page can override the SSR-provided title for the breadcrumb's last
  // segment via `useBreadcrumbTitle()` — used when the human-readable label
  // is only known after a client-side fetch (e.g. order-detail wants to
  // show the invoice number, not the opaque URL token).
  const breadcrumbTitleOverride = useBreadcrumbTitleOverride();
  const currentPageTitle = breadcrumbTitleOverride ?? shellData?.currentPageTitle;
  const loginRegisterInsertAfter = shellData?.loginRegisterInsertAfter;
  const shellMenuCategories = shellData?.menuCategories;

  const pathname = normalizePath(router?.asPath || router?.pathname || "/");

  const headerContentItems = useMemo(
    () => filterHeaderNavigation(navigationItems),
    [navigationItems],
  );
  const headerEntries = useMemo(
    () =>
      buildHeaderNavigationEntries(
        navigationItems,
        loginRegisterInsertAfter,
        signedIn,
      ),
    [loginRegisterInsertAfter, navigationItems, signedIn],
  );
  const menuItem = useMemo(
    () => headerContentItems.find((item) => isMenuNavigationItem(item)) ?? null,
    [headerContentItems],
  );
  const menuCategories = useMemo(
    () => mergeMenuCategories(headerContentItems, shellMenuCategories ?? []),
    [headerContentItems, shellMenuCategories],
  );
  const mobileNavigationEntries = useMemo(
    () =>
      splitMobileNavigationEntries(
        headerEntries as HeaderNavigationEntry[],
      ) as {
        quickLinks: HeaderNavigationContentEntry[];
        actions: HeaderNavigationActionEntry[];
      },
    [headerEntries],
  );
  const quickLinkEntries = mobileNavigationEntries.quickLinks;
  const actionEntries = mobileNavigationEntries.actions;
  const breadcrumbs = useMemo(
    () =>
      buildBreadcrumbs({
        pathname,
        navigationItems: headerContentItems,
        currentPageTitle,
        menuCategories: shellMenuCategories,
      }),
    [currentPageTitle, headerContentItems, pathname, shellMenuCategories],
  );

  useEffect(() => {
    if (!router?.events) {
      return undefined;
    }

    const handleRouteChange = () => {
      setMobileOpen(false);
      setExpandedKeys([]);
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router]);

  useEffect(() => {
    if (!mobileOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileOpen]);

  function handleToggleSection(key: string) {
    setExpandedKeys((currentKeys) =>
      currentKeys.includes(key)
        ? currentKeys.filter((currentKey) => currentKey !== key)
        : [...currentKeys, key],
    );
  }

  function handleCloseMobileNavigation() {
    setMobileOpen(false);
  }

  const useFullWidthLayout =
    layoutVariant === "home" || layoutVariant === "immersive";

  return (
    <>
      <header
        data-storefront-header
        className="storefront-bg-primary sticky top-0 z-50 isolate overflow-visible text-white shadow-[0_24px_70px_-38px_rgba(0,39,0,0.95)]"
      >
        <div className="absolute inset-x-0 bottom-0 h-px bg-white/10" />
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
          <div className="flex items-center justify-between gap-3 py-4 lg:py-[1.45rem]">
            <Link href="/" aria-label="Go to homepage" className="flex items-center">
              <Image
                src={site.desktopLogo}
                alt={site.brandName}
                width={212}
                height={47}
                className="h-auto w-[8.6rem] sm:w-[10.35rem] md:w-[13.25rem]"
                priority
              />
            </Link>

            <nav
              aria-label="Primary"
              className="hidden flex-1 items-center justify-center lg:flex"
            >
              <div className="flex items-center border-x border-white/14 bg-black/10 backdrop-blur-sm">
                {headerEntries.map((entry, index) => (
                  <div
                    key={entry.key}
                    className={index === 0 ? "" : "border-l border-white/14"}
                  >
                    {entry.kind === "content" ? (
                      <NavItem
                        item={entry.item}
                        menuCategories={
                          isMenuNavigationItem(entry.item) ? menuCategories : []
                        }
                      />
                    ) : (
                      <DesktopActionItem entry={entry} />
                    )}
                  </div>
                ))}
              </div>
            </nav>

            <div className="flex items-center gap-2 lg:min-w-[10rem] lg:justify-end">
              <button
                type="button"
                className="snipcart-checkout storefront-text-primary-dark relative hidden items-center gap-2 rounded-full border border-white/16 bg-white px-4 py-2.5 text-[0.95rem] font-semibold shadow-[0_14px_30px_-18px_rgba(0,0,0,0.54)] transition-colors duration-150 hover:bg-[#f8fbf7] lg:inline-flex"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                <span>Basket</span>
                <span className="storefront-bg-primary inline-flex min-w-6 items-center justify-center rounded-full px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
                  <span className="snipcart-items-count">0</span>
                </span>
              </button>

              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                className="inline-flex h-[2.85rem] w-[2.85rem] items-center justify-center rounded-full border border-white/16 bg-white/10 text-white transition-colors hover:bg-white/16 lg:hidden"
                aria-label="Open navigation menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  className="h-6 w-6"
                  aria-hidden="true"
                >
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div
          data-storefront-mobile-nav
          className={`fixed inset-0 z-50 lg:hidden ${
            mobileOpen ? "" : "pointer-events-none"
          }`}
          aria-hidden={!mobileOpen}
        >
          <button
            type="button"
            onClick={handleCloseMobileNavigation}
            aria-label="Close navigation overlay"
            className={`absolute inset-0 bg-[#001700]/55 backdrop-blur-sm transition-opacity duration-200 ${
              mobileOpen ? "opacity-100" : "opacity-0"
            }`}
          />
          <aside
            className={`storefront-bg-primary-dark absolute right-0 top-0 flex h-full w-full max-w-sm flex-col overflow-y-auto px-5 pb-8 pt-5 text-white shadow-[0_30px_80px_-24px_rgba(0,0,0,0.9)] transition-transform duration-300 ${
              mobileOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-4">
              <Link
                href="/"
                aria-label="Go to homepage"
                onClick={handleCloseMobileNavigation}
                className="inline-flex items-center py-1"
              >
                <Image
                  src={site.desktopLogo}
                  alt={site.brandName}
                  width={152}
                  height={34}
                  className="h-auto w-[9.5rem]"
                  priority
                />
              </Link>
              <button
                type="button"
                onClick={handleCloseMobileNavigation}
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/12 bg-white/8 text-lg text-white transition-colors hover:bg-white/14"
                aria-label="Close navigation menu"
              >
                &times;
              </button>
            </div>

            <button
              type="button"
              className="snipcart-checkout mt-4 flex w-full items-center justify-between rounded-[1.4rem] border border-white/12 bg-white/8 px-4 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/14"
            >
              <span className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <path d="M16 10a4 4 0 0 1-8 0" />
                </svg>
                <span>View basket</span>
              </span>
              <span className="storefront-bg-primary inline-flex min-w-6 items-center justify-center rounded-full px-1.5 py-0.5 text-[0.65rem] font-bold text-white">
                <span className="snipcart-items-count">0</span>
              </span>
            </button>

            {actionEntries.length > 0 ? (
              <div className="mt-3 space-y-2">
                {actionEntries.map((entry) => (
                  <MobileActionItem
                    key={entry.key}
                    entry={entry}
                    onNavigate={handleCloseMobileNavigation}
                  />
                ))}
              </div>
            ) : null}

            <section className="mt-5 rounded-[2rem] border border-white/10 bg-[linear-gradient(180deg,#f8fbf7_0%,#edf5eb_100%)] p-4 storefront-text-heading shadow-[0_24px_60px_-44px_rgba(0,0,0,0.55)]">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-[0.64rem] font-semibold uppercase tracking-[0.28em] storefront-text-muted">
                    Kitchen
                  </p>
                  <h2 className="mt-1.5 text-[1.45rem] font-semibold tracking-tight storefront-text-heading">
                    Explore the menu
                  </h2>
                  <p className="mt-1.5 text-sm leading-5 storefront-text-body">
                    Pick a category and jump straight into the dishes.
                  </p>
                </div>
                {menuItem ? (
                  <Link
                    href={menuItem.path}
                    onClick={handleCloseMobileNavigation}
                    className="storefront-text-primary shrink-0 pt-1 text-sm font-semibold"
                  >
                    Browse all
                  </Link>
                ) : null}
              </div>

              {menuCategories.length > 0 ? (
                <div className="mt-4 space-y-3">
                  {menuCategories.map((category) => (
                    <MobileCategoryLink
                      key={category.key}
                      category={category}
                      onNavigate={handleCloseMobileNavigation}
                    />
                  ))}
                </div>
              ) : menuItem ? (
                <div className="mt-4">
                  <Link
                    href={menuItem.path}
                    onClick={handleCloseMobileNavigation}
                    className="flex items-center justify-between rounded-[1.4rem] border storefront-border-soft bg-white px-4 py-3 text-sm font-semibold storefront-text-heading shadow-[0_18px_40px_-34px_rgba(0,39,0,0.24)]"
                  >
                    <span>{menuItem.label}</span>
                    <span aria-hidden="true" className="storefront-text-primary">
                      &rarr;
                    </span>
                  </Link>
                </div>
              ) : null}
            </section>

            {quickLinkEntries.length > 0 ? (
              <section className="mt-7 space-y-3">
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.26em] text-white/55">
                  Quick links
                </p>
                <div className="space-y-3">
                  {quickLinkEntries.map((entry) => (
                    <MobileTree
                      key={entry.key}
                      item={entry.item}
                      expandedKeys={expandedKeys}
                      onToggle={handleToggleSection}
                      onNavigate={handleCloseMobileNavigation}
                    />
                  ))}
                </div>
              </section>
            ) : null}

          </aside>
        </div>
      </header>

      {breadcrumbs.length > 0 ? (
        <div
          className="border-b border-white/8 backdrop-blur-sm"
          style={{ backgroundColor: "var(--storefront-breadcrumb-background)" }}
        >
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <StorefrontBreadcrumbs items={breadcrumbs} />
          </div>
        </div>
      ) : null}

      <main
        className={
          useFullWidthLayout
            ? "w-full overflow-x-clip"
            : "mx-auto w-full max-w-6xl px-4 pt-6 sm:px-6 sm:pt-8"
        }
      >
        {children}
      </main>

      <StorefrontFooter shellData={shellData} site={site} />
    </>
  );
}
